import React, { useMemo } from "react"

import { faCircleCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { INTEGRATIONS_CONFIG } from "../../../config/integrations.config"
import { EraUploadHelper } from "../../../helpers/eraUpload.helper"
import type { IEraUploadCompletionState } from "../../../types/integrations.types"

export const EraUploadCompleteComponent: React.FunctionComponent<
    IEraUploadCompleteComponentProps
> = props => {
    const nextIngestRunLabel = useMemo(() => {
        return EraUploadHelper.formatNextIngestRunLabel()
    }, [props.state.completedAt])

    const visibleFileNames = props.state.fileNames.slice(0, 5)

    const hiddenFileCount =
        props.state.fileNames.length - visibleFileNames.length

    return (
        <div className={`max-w-[960px] w-full ${props.className ?? ""}`}>
            <div
                className="
                    rounded-[8px] border border-solid border-brand-warmLight2
                    bg-brand-white px-[24px] py-[32px] md:px-[32px]
                "
            >
                <div className="flex items-start gap-[16px]">
                    <div
                        className="
                            shrink-0 w-[48px] h-[48px] rounded-full
                            bg-brand-greenLight flex items-center justify-center
                        "
                        aria-hidden
                    >
                        <FontAwesomeIcon
                            icon={faCircleCheck}
                            style={{
                                width: 24,
                                height: 24,
                                color: NIRVANA_COLORS.brand.greenDark,
                            }}
                        />
                    </div>

                    <div className="min-w-0 flex-1">
                        <PrimaryText typography="h4" weight="semibold">
                            {EraUploadHelper.getUploadCompleteTitle(
                                props.state,
                            )}
                        </PrimaryText>

                        <PrimaryText
                            className="mt-[8px] max-w-[640px]"
                            weight="medium"
                            color={NIRVANA_COLORS.brand.grey7}
                        >
                            {EraUploadHelper.getUploadCompleteDescription(
                                props.state,
                            )}
                        </PrimaryText>
                    </div>
                </div>

                <div
                    className="
                        mt-[24px] rounded-[8px] px-[16px] py-[14px]
                        bg-brand-lilacLight border border-solid border-brand-warmLight2
                    "
                >
                    <PrimaryText
                        typography="subtitleSemibold"
                        color={NIRVANA_COLORS.brand.blue}
                    >
                        ERA files ingest daily at{" "}
                        {INTEGRATIONS_CONFIG.eraUpload.dailyIngestRunHourEt}
                        :00 AM ET
                    </PrimaryText>

                    <PrimaryText
                        className="mt-[4px]"
                        size={13}
                        lineHeight={18}
                        color={NIRVANA_COLORS.brand.grey7}
                    >
                        {nextIngestRunLabel}
                    </PrimaryText>
                </div>

                <div className="mt-[24px]">
                    <PrimaryText typography="subtitleSemibold">
                        Recent upload
                    </PrimaryText>

                    <ul className="mt-[12px] list-none p-0 m-0 flex flex-col gap-[8px]">
                        {visibleFileNames.map((fileName, index) => {
                            return (
                                <li
                                    key={`${fileName}-${index}`}
                                    className="
                                        rounded-[8px] px-[16px] py-[12px]
                                        border border-solid border-brand-warmLight2
                                        bg-brand-offWhite
                                    "
                                >
                                    <PrimaryText
                                        className="break-all"
                                        weight="medium"
                                    >
                                        {fileName}
                                    </PrimaryText>
                                </li>
                            )
                        })}
                    </ul>

                    {hiddenFileCount > 0 ? (
                        <PrimaryText
                            className="mt-[8px]"
                            size={13}
                            lineHeight={18}
                            color={NIRVANA_COLORS.brand.grey7}
                        >
                            +{hiddenFileCount} more file
                            {hiddenFileCount === 1 ? "" : "s"}
                        </PrimaryText>
                    ) : null}
                </div>

                <div className="mt-[32px] flex flex-wrap gap-[12px]">
                    <ButtonElement
                        label="Upload more files"
                        htmlType="button"
                        type="primary"
                        onClick={props.onUploadMore}
                    />

                    <ButtonElement
                        label="View upload insights"
                        htmlType="button"
                        type="default"
                        onClick={props.onViewUploadedFiles}
                    />
                </div>
            </div>
        </div>
    )
}

interface IEraUploadCompleteComponentProps {
    className?: string
    state: IEraUploadCompletionState
    onUploadMore: () => void
    onViewUploadedFiles: () => void
}
